import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { ABOUT_DEFAULTS } from "@/lib/aboutContent";
import {
  ArrowLeft, Brain, Grid3x3, Type, HelpCircle, Camera, Pencil, Building2,
  Clock, Timer, CheckSquare, CalendarDays, Flame, Trophy, ShoppingBag, ShieldAlert, Sparkles,
} from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const fmtTime = (hhmm) => {
  if (!hhmm) return "—";
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ap} IST`;
};

const toMin = (hhmm) => { const [h, m] = (hhmm || "0:0").split(":").map(Number); return h * 60 + m; };

const fmtDays = (arr) => {
  if (!arr || arr.length === 0) return "—";
  const s = [...arr].sort((a, b) => a - b);
  const isRange = s.length > 1 && s.every((v, i) => i === 0 || v === s[i - 1] + 1);
  if (isRange) return `${DAYS[s[0]]} to ${DAYS[s[s.length - 1]]}`;
  return s.map((d) => DAYS[d]).join(", ");
};

const GAME_META = [
  { key: "rebus", icon: Brain, color: "#39FF14", title: "Rebus Daily" },
  { key: "sudoku", icon: Grid3x3, color: "#1E88E5", title: "Sudoku Daily" },
  { key: "word_building", icon: Type, color: "#FFD600", title: "Word Building" },
  { key: "trivia", icon: HelpCircle, color: "#FF3B30", title: "Trivia of the Day" },
  { key: "caption", icon: Camera, color: "#9C27B0", title: "Photo Caption" },
  { key: "pictionary", icon: Pencil, color: "#00BCD4", title: "Office Pictionary" },
  { key: "departments", icon: Building2, color: "#FF9800", title: "Department Battles", always: true },
];

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="brutal-card p-6" data-testid={testid}>
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 border-2 border-black flex items-center justify-center bg-[#FFD600]"><Icon size={20} className="text-black" /></div>
      <h3 className="text-xl md:text-2xl font-black uppercase tracking-tight">{title}</h3>
    </div>
    <div className="text-neutral-700 space-y-2 text-sm md:text-base">{children}</div>
  </div>
);

export default function About() {
  const navigate = useNavigate();
  const [r, setR] = useState(null);

  useEffect(() => {
    api.get("/public/rules").then((res) => setR(res.data)).catch(() => setR({}));
  }, []);

  const sc = r?.scoring || {};
  const nextDay = r && toMin(r.window_end) <= toMin(r.window_start);
  const A = {
    ...ABOUT_DEFAULTS,
    ...(r?.about || {}),
    games: { ...ABOUT_DEFAULTS.games, ...((r?.about || {}).games || {}) },
  };
  const games = GAME_META.filter((g) => g.always || (r?.games_enabled?.[g.key] ?? true));

  const tokens = {
    "{window_start}": fmtTime(r?.window_start),
    "{window_end}": `${fmtTime(r?.window_end)}${nextDay ? " the following day" : ""}`,
    "{play_minutes}": String(r?.daily_play_minutes ?? 30),
    "{max_submissions}": String(r?.max_submissions_per_day ?? 1),
    "{active_days}": fmtDays(r?.active_days),
  };
  const sub = (t) => Object.entries(tokens).reduce((s, [k, v]) => s.split(k).join(v), t || "");

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b-4 border-black px-6 py-4 flex items-center justify-between sticky top-0 bg-[#FDFBF7] z-10">
        <BrandLogo size="md" title="TekWissen Fun Zone" subtitle="About & Rules" />
        <button data-testid="about-back-btn" onClick={() => navigate(-1)} className="brutal-btn-secondary text-sm flex items-center gap-2">
          <ArrowLeft size={16} /> Back
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        <div>
          <div className="inline-block bg-[#39FF14] border-2 border-black px-3 py-1 brutal-shadow-sm mb-4">
            <span className="font-mono text-xs font-bold">RULES · GUIDELINES · TIMINGS</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tighter uppercase leading-none">About the Fun Zone</h1>
          <p className="text-lg mt-4 text-neutral-700 max-w-2xl">{A.intro}</p>
        </div>

        {/* Coming soon banner */}
        <div className="border-4 border-black bg-[#FFD600] p-5" data-testid="about-coming-soon">
          <div className="flex items-center gap-2 font-black uppercase mb-1"><Sparkles size={18} /> Coming Very Soon</div>
          <p className="text-sm font-semibold text-black">{A.coming_soon}</p>
        </div>

        {/* Games */}
        <div>
          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight mb-4">The Games</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {games.map((g) => (
              <div key={g.key} className="brutal-card p-5" data-testid={`about-game-${g.key}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 border-2 border-black flex items-center justify-center" style={{ background: g.color }}><g.icon size={20} className="text-black" /></div>
                  <h3 className="font-black uppercase tracking-tight">{g.title}</h3>
                </div>
                <p className="text-sm text-neutral-700">{A.games[g.key]}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Rules */}
        <div className="space-y-4">
          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight">Rules &amp; Guidelines</h2>

          <Section icon={Clock} title="Game Availability" testid="about-availability">
            <p>{sub(A.availability)}</p>
          </Section>

          <Section icon={Timer} title="Daily Play Time" testid="about-playtime">
            <p>{sub(A.playtime)}</p>
          </Section>

          <Section icon={CheckSquare} title="Daily Submissions" testid="about-submissions">
            <p>{sub(A.submissions)}</p>
          </Section>

          <Section icon={CalendarDays} title="Active Days" testid="about-active-days">
            <p>{sub(A.active_days_note)}</p>
          </Section>

          <Section icon={Flame} title="Daily Streaks" testid="about-streaks">
            <p>{A.streaks_intro}</p>
            <p>{A.streaks}</p>
          </Section>

          <Section icon={Trophy} title="Scoring System" testid="about-scoring">
            <p>{A.scoring_intro}</p>
            <div className="border-2 border-black mt-2">
              {[
                ["Correct answer (Rebus, Sudoku or Word Building)", `${sc.correct ?? 30} Points`],
                ["All 3 games solved on the same day", `+${sc.daily_all_three ?? 10} Bonus (= ${sc.daily_total ?? 100} Points/day)`],
                [`${sc.streak_days ?? 5}-day streak — all 3 games each day`, `+${sc.streak_bonus ?? 50} Bonus`],
              ].map(([label, pts], i) => (
                <div key={label} className={`flex justify-between gap-4 px-3 py-2 ${i % 2 ? "bg-neutral-50" : "bg-white"} ${i ? "border-t-2 border-black" : ""}`}>
                  <span className="font-semibold">{label}</span>
                  <span className="font-black whitespace-nowrap">{pts}</span>
                </div>
              ))}
            </div>
            <p className="mt-2">{A.scoring_note}</p>
          </Section>

          <Section icon={Trophy} title="Leaderboards" testid="about-leaderboards">
            <p>{A.leaderboards}</p>
          </Section>

          <Section icon={ShoppingBag} title="Merchandise Shop" testid="about-shop">
            <p><span className="inline-block bg-[#FFD600] border border-black px-2 py-0.5 text-xs font-black uppercase mr-1">Coming Soon</span> {A.shop_intro}</p>
            <p>{A.shop_note}</p>
          </Section>

          <Section icon={ShieldAlert} title="General Guidelines" testid="about-general">
            <p>{A.general}</p>
            <p className="font-black text-[#FF3B30] border-2 border-[#FF3B30] bg-[#FFF0EF] p-3">{A.cheating}</p>
            <p>{A.general_footer}</p>
          </Section>
        </div>

        <div className="text-center pt-4">
          <button data-testid="about-play-btn" onClick={() => navigate("/play")} className="brutal-btn text-base">Start Playing</button>
        </div>
      </main>

      <footer className="border-t-4 border-black px-6 py-6 text-center text-sm text-neutral-600">
        TekWissen Employee Fun Zone · Where work meets play
      </footer>
    </div>
  );
}
